import useSQLite from '../../useSQLite'
import { defineSqliteFeature } from '../define-feature'

const { getItem, setItem, removeItem, listEntries } = useSQLite()

export const REINFORCE_MEMORY_PREFIX = 'reinforces:'
export const REINFORCE_DOWNLOAD_RESUME_PREFIX = 'resume:'
export const REINFORCE_DOWNLOAD_DURATION_PREFIX = 'duration:'
export const REINFORCE_DOWNLOAD_CHUNKS_PREFIX = 'chunks:'

const REINFORCE_DOWNLOAD_PREFIXES = [
    REINFORCE_DOWNLOAD_RESUME_PREFIX,
    REINFORCE_DOWNLOAD_DURATION_PREFIX,
]

export const makeReinforceMemoryKey = (suffix) => `${REINFORCE_MEMORY_PREFIX}${suffix}`

export const stripReinforceMemoryPrefix = (key) =>
    key && key.startsWith(REINFORCE_MEMORY_PREFIX) ? key.slice(REINFORCE_MEMORY_PREFIX.length) : key

export const makeReinforceDownloadResumeKey = (filename) =>
    makeReinforceMemoryKey(`${REINFORCE_DOWNLOAD_RESUME_PREFIX}${filename}`)

export const makeReinforceDownloadDurationKey = (filename) =>
    makeReinforceMemoryKey(`${REINFORCE_DOWNLOAD_DURATION_PREFIX}${filename}`)

export const makeReinforceDownloadChunksKey = (filename) =>
    makeReinforceMemoryKey(`${REINFORCE_DOWNLOAD_CHUNKS_PREFIX}${filename}`)

export async function readReinforceMemory(suffix, defaultValue = null) {
    if (!suffix) return defaultValue
    return getItem(makeReinforceMemoryKey(suffix), defaultValue)
}

export async function writeReinforceMemory(suffix, value) {
    if (!suffix) return
    await setItem(makeReinforceMemoryKey(suffix), value)
}

export async function removeReinforceMemory(suffix) {
    if (!suffix) return
    await removeItem(makeReinforceMemoryKey(suffix))
}

export async function listReinforceMemoryKeys() {
    const entries = await listEntries({ prefix: REINFORCE_MEMORY_PREFIX })
    return entries.map((entry) => entry.key)
}

export const isReinforceDownloadMetaSuffix = (suffix) => {
    if (!suffix) return false
    return REINFORCE_DOWNLOAD_PREFIXES.some((prefix) => suffix.startsWith(prefix))
}

export const isReinforceDownloadMetaKey = (key) => {
    const suffix = stripReinforceMemoryPrefix(key)
    return isReinforceDownloadMetaSuffix(suffix)
}

export const extractReinforceDownloadFilename = (key) => {
    const suffix = stripReinforceMemoryPrefix(key)
    if (!suffix) return null
    const match = suffix.match(/^(?:duration|resume):(.+)$/)
    return match?.[1] || null
}

export async function listReinforceDownloadMetaKeys() {
    const keys = await listReinforceMemoryKeys()
    return keys.filter(isReinforceDownloadMetaKey)
}

export async function listReinforceDownloadMetaFileNames() {
    const keys = await listReinforceDownloadMetaKeys()
    const names = new Set()
    for (const key of keys) {
        const name = extractReinforceDownloadFilename(key)
        if (name) names.add(name)
    }
    return Array.from(names)
}

export async function clearReinforceDownloadMetaFor(filename) {
    if (!filename) return
    await Promise.all([
        removeReinforceMemory(`${REINFORCE_DOWNLOAD_RESUME_PREFIX}${filename}`),
        removeReinforceMemory(`${REINFORCE_DOWNLOAD_DURATION_PREFIX}${filename}`),
    ])
}

export const wrapCachedNumber = (value) => ({
    value: Number(value),
    updatedAt: Date.now(),
})

export const readCachedNumber = (raw) => {
    if (raw && typeof raw === 'object') {
        const candidate = raw.value ?? raw.number ?? raw.data
        const num = Number(candidate)
        return Number.isFinite(num) ? num : null
    }
    const num = Number(raw)
    return Number.isFinite(num) ? num : null
}

export const readCachedUpdatedAt = (raw) => {
    if (!raw || typeof raw !== 'object') return null
    const num = Number(raw.updatedAt)
    return Number.isFinite(num) ? num : null
}

const REINFORCE_TTL_SEC = 3 * 86400
const NOOP = async () => {}

export default defineSqliteFeature({
    key: 'reinforceMemory',
    purgeOnLogout: true,
    ensureSchema: NOOP,
    onBoot: ({ pruneKeys }) => pruneKeys({ prefix: REINFORCE_MEMORY_PREFIX, maxAgeSec: REINFORCE_TTL_SEC }),
    getSummary: ({ getKeyStats }) => getKeyStats({ prefix: REINFORCE_MEMORY_PREFIX }),
    purge: ({ pruneKeys }) => pruneKeys({ prefix: REINFORCE_MEMORY_PREFIX }),
    fallbacks: {
        onBoot: { ok: false, cleared: 0 },
        getSummary: null,
        purge: { ok: false, cleared: 0 },
    },
})
